import { DTD } from '~/lib/site/constants'

/**
 * Loads a third-party script once (client only) and removes nothing on
 * unmount. Resolves when it has loaded, immediately if an earlier call already
 * did. Rejects if it fails to load; the dead tag is removed, so a later call
 * tries again instead of waiting on a script that never fires `load`.
 */
export function loadScript(src: string): Promise<void> {
  let el = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`)
  if (el?.dataset.loaded === 'true') return Promise.resolve()
  if (!el) {
    const created = document.createElement('script')
    created.src = src
    created.async = true
    created.addEventListener('load', () => {
      created.dataset.loaded = 'true'
    })
    created.addEventListener('error', () => created.remove())
    document.body.appendChild(created)
    el = created
  }
  const script = el
  return new Promise((resolve, reject) => {
    script.addEventListener('load', () => resolve(), { once: true })
    script.addEventListener(
      'error',
      () => reject(new Error(`Failed to load ${src}`)),
      { once: true },
    )
  })
}

/**
 * Loads the Double the Donation plugin. 'search' is the employer-matching
 * search box (the script reads DDCONF and fills #dd-container); 'payroll'
 * renders the payroll-giving sections into #dtd-plugin.
 */
export async function loadDoubleTheDonation(mode: 'search' | 'payroll') {
  if (mode === 'search') window.DDCONF = { API_KEY: DTD.key }
  try {
    await loadScript(DTD.scriptUrl)
  } catch (err) {
    console.error(err)
    return
  }
  const plugin = window.doublethedonation?.plugin
  if (mode === 'search') {
    // The script fills #dd-container on `readystatechange` -> complete. That
    // has already happened when it loads after the page did (and on every
    // client-side visit, where the script is cached and the container new), so
    // it has to be asked to render. While the page is still loading, the
    // script's own listener does it, and doing it here too would render twice.
    const node = document.getElementById('dd-container')
    if (node && document.readyState === 'complete') plugin?.load_plugin(node)
    return
  }
  const node = document.getElementById('dtd-plugin')
  if (node && !node.hasChildNodes()) {
    plugin?.v2.load_plugin(node, DTD.key, {
      sections: ['payroll-giving', 'match', 'volunteer'],
    })
  }
}
